import 'bootstrap/dist/js/bootstrap.bundle.min';
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';

import { useAppSelector } from 'app/hooks';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { GOOGLE_OAUTH_ID } from 'common/constants';
import 'common/styles/backoffice.scss';
import 'common/styles/index.scss';

import AppLayout from 'common/components/AppLayout/AppLayout';
import Explore from 'features/explore/Explore';
import Landing from 'features/landing/Landing';
import Profile from 'features/profile/Profile';
import SchoolDetail from 'features/explore/school/SchoolDetail';
import ProgramDetail from 'features/explore/program/ProgramDetail';
import CountryDetail from 'features/explore/country/CountryDetail';
import Register from 'features/session/Register';
import Login from 'features/session/Login';
import UserInformation from 'features/user/account/UserInformation';
import AccountSettings from 'features/user/account/AccountSettingsForm';
import PreferencesForm from 'features/user/account/PreferencesForm';
import PostulationSetup from 'features/user/postulation/Postulation';
import EnrollSetup from 'features/user/Enroll';
import { SessionState } from 'features/session/sessionSlice';
import BackofficeDashboard from 'features/backoffice/Home';
import BackofficeLogin from 'features/backoffice/BackofficeLogin';
import BackofficeLayout from 'features/backoffice/BackofficeLayout/Layout';
import BackofficePrograms from 'features/backoffice/Program/Programs';
import BackofficeProgramDetail from 'features/backoffice/Program/ProgramDetail';
import BackofficeSchools from 'features/backoffice/School/Schools';
import BackofficeSchoolDetail from 'features/backoffice/School/SchoolDetail';
import BackofficeCountries from 'features/backoffice/Country/Countries';
import BackofficeCountryDetail from 'features/backoffice/Country/CountryDetail';
import BackofficeCities from 'features/backoffice/City/Cities';
import BackofficeCityDetail from 'features/backoffice/City/CityDetail';
import BackofficeUsers from 'features/backoffice/User/Users';
import Postulations from 'features/backoffice/Postulation/Postulations';
import BackofficePostulationDetail from 'features/backoffice/Postulation/Postulation';
import ProgramForm from 'features/backoffice/Program/ProgramForm';
import SchoolForm from 'features/backoffice/School/SchoolForm';
import SchoolPhotos from 'features/backoffice/School/SchoolPhotos';
import ProgramPhotos from 'features/backoffice/Program/ProgramPhotos';
import Tags from 'features/backoffice/Tag/Tags';
import RequestMeeting from 'common/components/RequestMeeting';
import VariableLanding from 'features/landings/components/VariableLanding';
import DocumentLeads from 'features/backoffice/DocumentLead/DocumentLeads';
import PersonalStatementBuilderIndex from 'features/personal_statement_builder/PersonalStatementBuilderIndex';
import { PersonalStatementBuilder } from 'features/personal_statement_builder/PersonalStatementBuilder';
import LandingForm from 'features/backoffice/Landing/LandingForm';
import LandingPreview from 'features/backoffice/Landing/LandingPreview';
import PersonalStatementHub from 'features/personal_statement_builder/PersonalStatementHub';
import Landings from 'features/backoffice/Landing/Landings';
import ExploreDegreeOptions from 'features/explore/ExploreSelector';
import BackofficeSubjects from 'features/backoffice/Subject/Subjects';
import BackofficeSubjectForm from 'features/backoffice/Subject/SubjectForm';
import UserDashboardLayout from 'features/user/dashboard/layout/Layout';
import UserDashboard from 'features/user/dashboard/pages/dashboard/Dashboard';
import UserShorlist from 'features/user/dashboard/pages/shorlist';
import UserApplications from 'features/user/dashboard/pages/applications';
interface RouteProps {
  children: React.ReactElement;
}

const ProtectedRoute = ({ children }: RouteProps): React.ReactElement => {
  const { logged } = useAppSelector(({ session }: { session: SessionState }) => session);
  if (!logged) {
    return <Navigate to="/" replace />;
  }
  return children;
};

const PublicRoute = ({ children }: RouteProps): React.ReactElement => {
  const { logged } = useAppSelector(({ session }: { session: SessionState }) => session);
  const [searchParams] = useSearchParams();
  const programIdToApply = searchParams.get('apply_pid');
  if (logged && !programIdToApply) {
    return <Navigate to="/home" replace />;
  }
  return children;
};
const App = (): React.ReactElement => {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_OAUTH_ID}>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<AppLayout />}>
            <Route index element={<Landing />} />
            <Route path="explore/*" element={<Explore />} />
            <Route path="profile/*" element={<Profile />} />
            <Route path="personal-statement-builder" element={<PersonalStatementBuilderIndex />} />
            <Route
              path="personal-statements/:id"
              element={
                <ProtectedRoute>
                  <PersonalStatementBuilder />
                </ProtectedRoute>
              }
            />
            <Route
              path="personal-statements"
              element={
                <ProtectedRoute>
                  <PersonalStatementHub />
                </ProtectedRoute>
              }
            />
            <Route
              path="signup/*"
              element={
                <PublicRoute>
                  <Register />
                </PublicRoute>
              }
            />
            <Route
              path="login/*"
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              }
            />
            {/* <Route path="experts/meetings/patricia" element={<ScheduleMeeting />} /> */}
            <Route path="/request-meeting" element={<RequestMeeting />} />
            <Route path="experts/request-meeting/*" element={<RequestMeeting />} />
            <Route path="/home" element={<UserDashboardLayout />}>
              <Route index element={<UserDashboard />} />
              <Route path="personal_information/*" element={<UserInformation />} />
              <Route path="shorlist" element={<UserShorlist />} />
              <Route path="account_settings/*" element={<AccountSettings />} />
              <Route path="preferences/*" element={<PreferencesForm />} />
              <Route path="applications" element={<UserApplications />} />
              <Route path="applications/:id" element={<PostulationSetup />} />
              <Route path="enroll" element={<EnrollSetup />} />
            </Route>
            <Route path="programs/:programSlug/*" element={<ProgramDetail />} />
            <Route path="schools/:schoolSlug/*" element={<SchoolDetail />} />

            <Route path="explore/countries/:countrySlug/*" element={<CountryDetail />} />
            <Route path="landings/:landingSlug" element={<VariableLanding />} />
            {/* <Route path="zoho-form" element={<ZohoForm />} /> */}
            {/* <Route path="own-form" element={<OwnForm />} /> */}
            <Route path="/:landingSlug" element={<VariableLanding />} />
            <Route path="/explore" element={<ExploreDegreeOptions />} />
          </Route>
          {/* Backfoffice ROUTES */}
          <Route path="/backoffice/login" element={<BackofficeLogin />} />
          <Route path="/backoffice" element={<BackofficeLayout />}>
            <Route path="dashboard" element={<BackofficeDashboard />} />
            <Route path="programs" element={<BackofficePrograms />} />
            <Route path="programs/new" element={<ProgramForm />} />
            <Route path="programs/:id" element={<BackofficeProgramDetail />} />
            <Route path="programs/:id/edit" element={<ProgramForm />} />
            <Route path="programs/:id/edit/photos" element={<ProgramPhotos />} />
            <Route path="schools" element={<BackofficeSchools />} />
            <Route path="schools/new" element={<SchoolForm />} />
            <Route path="schools/:id" element={<BackofficeSchoolDetail />} />
            <Route path="schools/:id/edit" element={<SchoolForm />} />
            <Route path="schools/:id/edit/photos" element={<SchoolPhotos />} />
            <Route path="countries" element={<BackofficeCountries />} />
            <Route path="countries/:id" element={<BackofficeCountryDetail />} />
            <Route path="cities" element={<BackofficeCities />} />
            <Route path="cities/:id" element={<BackofficeCityDetail />} />
            <Route path="documentLeads" element={<DocumentLeads />} />{' '}
            <Route path="landings" element={<Landings />} />
            <Route path="landings/:id/edit" element={<LandingForm />} />
            <Route path="landings/new" element={<LandingForm />} />
            <Route path="landings/:id/preview" element={<LandingPreview />} />
            <Route path="tags" element={<Tags />} />
            <Route path="users" element={<BackofficeUsers />} />
            <Route path="users/:id" element={<UserDashboard />} />
            <Route path="users/:id/applications/:postulation_id" element={<PostulationSetup />} />
            <Route path="users/:id/personal_information/*" element={<UserInformation />} />
            <Route path="applications" element={<Postulations />} />
            <Route path="applications/:id" element={<BackofficePostulationDetail />} />
            <Route path="subjects" element={<BackofficeSubjects />} />
            <Route path="subjects/new" element={<BackofficeSubjectForm />} />
            <Route path="subjects/:id/edit" element={<BackofficeSubjectForm />} />
          </Route>
          <Route path="/backoffice/landings/preview/:landingSlug" element={<LandingPreview />} />
        </Routes>
      </BrowserRouter>
    </GoogleOAuthProvider>
  );
};

export default App;
